import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MessageCircle, HelpCircle, Mail, X, Headphones } from "lucide-react";

const suggestions = [
  {
    icon: Calendar,
    title: "Plan een kennismaking",
    description: "Zie in 20 minuten hoe studie connect werkt voor jouw school.",
    action: () => ({ type: "navigate", to: "/demo" } as const),
  },
  {
    icon: MessageCircle,
    title: "Stel een vraag",
    description: "We reageren meestal binnen één werkdag.",
    action: () => ({ type: "navigate", to: "/contact" } as const),
  },
  {
    icon: HelpCircle,
    title: "Veelgestelde vragen",
    description: "Vind snel antwoord op praktische vragen.",
    action: () => ({ type: "navigate", to: "/veelgestelde-vragen" } as const),
  },
  {
    icon: Mail,
    title: "Mail ons",
    description: "info@studie-connect.nl",
    action: () => ({ type: "mailto", to: "mailto:info@studie-connect.nl" } as const),
  },
];

const HexagonIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 2l8.66 5v10L12 22l-8.66-5V7L12 2z" />
    <path d="M8 10c0-1 .8-2 2-2h4c1.2 0 2 1 2 2v4c0 1-.8 2-2 2h-4c-1.2 0-2-1-2-2v-4z" />
  </svg>
);

const HelpWidget = () => {
  const [open, setOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setShowHint(true), 2500);
    const hideTimer = setTimeout(() => setShowHint(false), 8500);
    return () => {
      clearTimeout(timer);
      clearTimeout(hideTimer);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  const handleSuggestion = (action: ReturnType<typeof suggestions[number]["action"]>) => {
    setOpen(false);
    if (action.type === "navigate") {
      navigate(action.to);
    } else {
      window.location.href = action.to;
    }
  };

  return (
    <div
      ref={panelRef}
      className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3"
      role="dialog"
      aria-label="Hulp en contact"
      aria-expanded={open}
    >
      {open && (
        <div className="w-[calc(100vw-2.5rem)] max-w-[22rem] bg-card border border-border rounded-3xl shadow-large p-5 animate-in slide-in-from-bottom-3 fade-in duration-200">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground shrink-0">
                <Headphones className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-extrabold text-secondary text-base leading-tight">
                  Hoe kunnen we helpen?
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Kies een snelle route naar antwoord of advies.
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Sluit hulpmenu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2">
            {suggestions.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.title}
                  onClick={() => handleSuggestion(item.action())}
                  className="w-full text-left group flex items-start gap-3 rounded-2xl p-3 hover:bg-accent transition-colors"
                >
                  <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-secondary group-hover:text-accent-foreground transition-colors">
                      {item.title}
                    </div>
                    <div className="text-xs text-muted-foreground leading-snug">
                      {item.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

        </div>
      )}

      <div className="relative flex items-center gap-3">
        {!open && showHint && (
          <button
            onClick={() => setOpen(true)}
            className="hidden sm:flex items-center bg-card border border-border text-secondary text-sm font-bold px-4 py-2.5 rounded-2xl shadow-medium animate-in fade-in slide-in-from-right-2 duration-300 hover:bg-accent transition-colors"
          >
            Hoe kunnen we helpen?
          </button>
        )}

        <button
          onClick={() => setOpen((v) => !v)}
          className={`
            w-14 h-14 rounded-3xl flex items-center justify-center shadow-large
            transition-all duration-300 hover:scale-105 active:scale-95
            ${open ? "bg-secondary text-secondary-foreground rotate-90" : "bg-primary text-primary-foreground"}
          `}
          aria-label={open ? "Sluit hulpmenu" : "Open hulpmenu"}
        >
          {open ? (
            <X className="w-6 h-6" />
          ) : (
            <HexagonIcon className="w-7 h-7" />
          )}
        </button>
      </div>
    </div>
  );
};

export default HelpWidget;
